/* VersatileGrid.tsx
 * A flexible data grid component that supports:
 * - Sortable columns
 * - Advanced search functionality
 * - Pagination
 * - Dynamic column widths
 * - Custom cell rendering
 * - Custom header content
 *
 * This component is designed to handle both simple and complex data sets
 * while maintaining consistent performance through pagination and
 * optimized rendering strategies.
 */

import React, { useState, useRef, useEffect, useMemo, ReactNode } from 'react'
import { Input, InputGroup } from 'rsuite'
import CustomIcons from './CustomIcons'
import SearchIcon from '@rsuite/icons/Search'
import {
  GridWrapper,
  GridTop,
  GridBottom,
  GridContainer,
  CellContent,
  SortIcon,
  HeaderCell,
  Cell,
  PaginationContainer,
  PageIndicator,
  NumberButton,
  GridHeaderContainer,
  GridHeaderLeft,
  GridHeaderRight,
  PagesContainer,
  NavigationButton,
  GridArea,
  NoItemsMessage,
  CellKeeper,
  EmptySpace,
} from './styles/VersatileGrid.styles'
//import font awsome
import theme from '../../styles/theme'
import { performSearch } from './logic/SearchLogic'
import { stringifyForSearch } from './logic/SearchLogic'
import {
  ExpansiveListInVersatileGrid,
  SmoothFullWidthCell,
} from './VersatileGridUtils'
import UniversalLoader from './UniversalLoader'

// TypeScript interfaces
export interface Header {
  title: string
  key: string
  type: 'string' | 'number' | 'boolean' | 'datetime'
  isSortable?: boolean
  isSearchable?: boolean
  center?: boolean
  borderLeft?: boolean
  overrideJSX?: ReactNode
  style?: React.CSSProperties
  headerCellStyle?: React.CSSProperties
  cellStyle?: React.CSSProperties
  cellContentStyle?: React.CSSProperties
  cellKeeperStyle?: React.CSSProperties
  valueMapping?: { [key: string]: string } | { [key: number]: string }
  searchRankingRule?: (keyword: string, value: any) => number
  getSortValue?: (item: any) => any
  /**
   * Backend ordering field for server-side mode. Falls back to `key` when unset.
   * Only used when `VersatileGrid` runs with `serverSide={true}`.
   */
  serverKey?: string
}

interface DataItem {
  [key: string]: any
  _searchId: string
  _originalIndex?: number
  _intersectingData?: any
}

interface HighlightConfig {
  idx: number
  color: string
}

interface VersatileGridProps {
  headers: Header[]
  data: DataItem[]
  renderRow: (
    rowData: DataItem,
    rowIdx: number,
    originalIndex: number,
  ) => ReactNode[]
  headerInfo?: ReactNode
  maxDisplayedItems?: number
  gridContainerStyle?: React.CSSProperties
  renderIntersectingRow?: (
    rowData: DataItem,
    rowIdx: number,
    originalIndex: number,
  ) => ReactNode | null
  dataForIntersecting?: any[]
  searchPlaceholder?: string
  isSearchable?: boolean
  highlightDataIdx?: number | HighlightConfig[] | null
  initialSearchTerm?: string
  onFilteredDataChange?: ((filteredData: DataItem[]) => void) | null
  loading?: boolean
  loadingMessage?: string
  emptyStateMessage?: string
  /**
   * Opt-in server-side mode (backward compatible, default `false`).
   * When `true`, the grid does NOT sort/filter/slice locally. The caller owns
   * fetching: `data` is the current page, `serverTotal` the backend `total`,
   * `serverPage` the 1-based backend `page`. Optional `serverLastPage` is the
   * backend `last_page` when that is not `ceil(total / pageSize)`. Search
   * input is debounced (~300ms)
   * and forwarded via `onSearchChange`; header clicks via `onSortChange`;
   * page clicks via `onPageChange` (all 1-based where applicable).
   */
  serverSide?: boolean
  serverTotal?: number
  serverPage?: number
  serverLastPage?: number
  onPageChange?: (page: number) => void
  onSearchChange?: (term: string) => void
  onSortChange?: (
    sort: { key: string; direction: SortDirection } | null,
  ) => void
  /** Tighter pager for narrow grids (dashboard last-saved lists). */
  compactPagination?: boolean
  'data-testid'?: string
  [key: string]: any // For ...props
}

type PageUpdater = (currentPage: number) => number
type SortDirection = 'asc' | 'desc'
type DataType = 'string' | 'number' | 'boolean' | 'datetime'

/* Data comparison utilities for sorting different data types
 * These functions normalize different data types for consistent sorting behavior
 */
const compareValues = (a: any, b: any, type: DataType): number => {
  if (a === b) return 0
  if (a === null || a === undefined) return 1
  if (b === null || b === undefined) return -1

  switch (type) {
    case 'number':
      return Number(a) - Number(b)
    case 'boolean':
      return a === b ? 0 : a ? -1 : 1
    case 'datetime':
      return new Date(a).getTime() - new Date(b).getTime()
    case 'string':
    default:
      // For arrays and objects, stringify them first
      const strA = typeof a === 'object' ? stringifyForSearch(a) : String(a)
      const strB = typeof b === 'object' ? stringifyForSearch(b) : String(b)

      return strA.localeCompare(strB)
  }
}

/* Data sorting utility
 * Handles sorting of data array based on column configuration
 */
const sortData = (
  data: DataItem[],
  key: string,
  type: DataType,
  direction: SortDirection,
  header?: Header,
): DataItem[] => {
  return [...data].sort((a, b) => {
    // Use getSortValue if provided in header config
    if (header && typeof header.getSortValue === 'function') {
      const valueA = header.getSortValue(a)
      const valueB = header.getSortValue(b)
      const result = compareValues(valueA, valueB, type)

      return direction === 'asc' ? result : -result
    }

    // Otherwise use standard comparison
    const result = compareValues(a[key], b[key], type)

    return direction === 'asc' ? result : -result
  })
}

/* Pagination utilities
 * Generates page numbers with ellipsis for large data sets
 * Shows direct navigation for nearby pages and shortcuts for distant ones
 */
const getPageNumbers = (
  currentPage: number,
  totalPages: number,
  compact = false,
): (number | string)[] => {
  const allThreshold = compact ? 3 : 5
  // If we have few pages, just show all of them
  if (totalPages <= allThreshold) {
    return Array.from({ length: totalPages }, (_, i) => i)
  }

  const delta = compact ? 0 : 2 // Nearby pages around the current one
  const range: number[] = []
  const rangeWithDots: (number | string)[] = []

  // Always show first page
  range.push(0)

  for (let i = currentPage - delta; i <= currentPage + delta; i++) {
    if (i > 0 && i < totalPages - 1) {
      range.push(i)
    }
  }

  // Always show last page
  if (totalPages > 1) {
    range.push(totalPages - 1)
  }

  // Sort and remove duplicates
  const uniqueRange = [...new Set(range)].sort((a, b) => a - b)

  // Add ellipsis where needed. Start at -1 so page 0 is not treated as a gap.
  let prev = -1

  for (const i of uniqueRange) {
    if (i - prev === 2) {
      rangeWithDots.push(prev + 1)
    } else if (i - prev !== 1) {
      rangeWithDots.push('...')
    }

    rangeWithDots.push(i)
    prev = i
  }

  return rangeWithDots
}

const checkForExpansiveList = (cells: ReactNode[]): boolean => {
  return cells.some(
    (cell) =>
      React.isValidElement(cell) && cell.type === ExpansiveListInVersatileGrid,
  )
}

/* Main grid component */
const VersatileGrid: React.FC<VersatileGridProps> = ({
  headers,
  data,
  renderRow,
  headerInfo,
  maxDisplayedItems = 10,
  gridContainerStyle,
  renderIntersectingRow,
  dataForIntersecting,
  searchPlaceholder = 'Search...',
  isSearchable = true,
  highlightDataIdx = null, // New prop
  initialSearchTerm = '', // Add prop to initialize search
  onFilteredDataChange = null, // New prop for callback when filtered data changes
  loading = false, // New prop for loading state
  loadingMessage = 'Loading...', // New prop for loading message
  emptyStateMessage = 'No items', // New prop for empty state message
  paddingInGridHeader = '1.5rem 3rem 1.5rem 2rem', // New prop for custom grid header padding
  'data-testid': dataTestId, // Add data-testid support
  // Server-side mode props (opt-in, default = client-side behavior)
  serverSide = false,
  serverTotal,
  serverPage = 1,
  serverLastPage,
  onPageChange,
  onSearchChange,
  onSortChange,
  compactPagination = false,
  ...props // Capture any other props
}) => {
  const columns = headers.length
  const [activeSort, setActiveSort] = useState<number | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const currentPageRef = useRef<number>(0)
  const searchTermRef = useRef<string>(initialSearchTerm || '')
  const [searchTerm, setSearchTerm] = useState<string>(searchTermRef.current)
  const [, setSearchScores] = useState<Map<string, number>>(new Map())
  const gridRef = useRef<HTMLDivElement>(null)
  const [calculatedGridTemplate] = useState<string | null>(null)
  const gridContainerRef = useRef<HTMLDivElement>(null)
  const [, updateState] = useState<object>({})
  const forceUpdate = () => updateState({})
  const lastFilterCallRef = useRef<number>(0)
  const onSearchChangeRef = useRef(onSearchChange)
  onSearchChangeRef.current = onSearchChange
  const userChangedSearchRef = useRef(false)

  // Helper functions for safe get/set of current page
  const getCurrentPage = (): number => currentPageRef.current

  // Reset page to first page when search term changes from user input
  useEffect(() => {
    if (searchTermRef.current !== searchTerm) {
      currentPageRef.current = 0 // Reset to first page on new search
      searchTermRef.current = searchTerm // Update ref with new value
    }
  }, [searchTerm])

  // Ensure data has IDs and augment with _originalIndex
  const dataWithIds = useMemo((): DataItem[] => {
    return data.map((item, idx) => ({
      ...item,
      _searchId: `id_${idx}`,
      _originalIndex: idx,
    }))
  }, [data])

  // Create a combined dataset that links main data with intersecting data
  const combinedData = useMemo((): DataItem[] => {
    if (!dataForIntersecting) return dataWithIds

    return dataWithIds.map((item, idx) => ({
      ...item,
      _intersectingData: dataForIntersecting[idx] || {},
    }))
  }, [dataWithIds, dataForIntersecting])

  // Initialize sortedData with filtered data if there's a search term
  const [sortedData, setSortedData] = useState<DataItem[]>(() => {
    if (!searchTermRef.current) return dataWithIds
    const { results, scores } = performSearch(
      combinedData,
      headers,
      searchTermRef.current,
    )

    setSearchScores(scores)

    return results
      .map((id) => combinedData.find((item) => item._searchId === id))
      .filter(Boolean) as DataItem[]
  })

  /* Search handling (client-side mode only)
   * Processes search input and updates grid data immediately
   * Uses advanced search logic for multi-field matching
   * In server-side mode the term is debounced and forwarded via onSearchChange instead.
   */
  useEffect(() => {
    if (serverSide) return
    const { results, scores } = performSearch(combinedData, headers, searchTerm)
    const filteredData = results
      .map((id) => combinedData.find((item) => item._searchId === id))
      .filter(Boolean) as DataItem[]

    setSearchScores(scores)
    setSortedData(filteredData)

    // Only call callback when filtered data actually changes
    if (onFilteredDataChange && filteredData.length > 0) {
      // Using a timestamp to prevent calling the callback on every render
      const currentTime = new Date().getTime()

      if (
        !lastFilterCallRef.current ||
        currentTime - lastFilterCallRef.current > 100
      ) {
        onFilteredDataChange(filteredData)
        lastFilterCallRef.current = currentTime
      }
    }
  }, [searchTerm, combinedData, headers, onFilteredDataChange, serverSide])

  /* Server-side search forwarding (server-side mode only)
   * Debounces the input term so the caller can fetch `?search=` without
   * spamming the API on every keystroke. Page reset is the caller's job.
   * Only forward after the user actually types — mount/remount must not
   * look like a new search (that would reset the parent page).
   */
  useEffect(() => {
    if (!serverSide) return
    if (!userChangedSearchRef.current) return
    const timer = setTimeout(() => {
      onSearchChangeRef.current?.(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm, serverSide])

  // Calculate pagination values
  // Client mode: paginate the locally filtered/sorted data.
  // Server mode: `data` is already the current page; paginate via backend total.
  const effectiveTotal = serverSide
    ? (serverTotal ?? data.length)
    : sortedData.length
  const totalPages =
    serverSide && serverLastPage != null && serverLastPage > 0
      ? serverLastPage
      : Math.ceil(effectiveTotal / maxDisplayedItems)

  // Ensure current page is valid for the current data
  useEffect(() => {
    if (totalPages > 0 && getCurrentPage() >= totalPages) {
      // If current page is now out of bounds, set to the last available page
      currentPageRef.current = totalPages - 1
    }
  }, [totalPages])

  const startIndex = getCurrentPage() * maxDisplayedItems
  const endIndex = startIndex + maxDisplayedItems

  // Get current page data (client mode slices the sorted data;
  // server mode uses the page the caller already fetched)
  const currentData = sortedData.slice(startIndex, endIndex)
  const displayData = serverSide ? dataWithIds : currentData
  // Server `page` is 1-based, internal pages are 0-based
  const currentPageZeroBased = serverSide
    ? Math.max(0, (serverPage ?? 1) - 1)
    : getCurrentPage()

  const setCurrentPage = (pageOrUpdater: number | PageUpdater): void => {
    if (typeof pageOrUpdater === 'function') {
      currentPageRef.current = pageOrUpdater(currentPageRef.current)
    } else {
      currentPageRef.current = pageOrUpdater
    }

    // Force a re-render
    forceUpdate()
  }

  /* Page navigation that works in both modes.
   * Client mode updates the internal page ref; server mode notifies the caller
   * with a 1-based page number so it can fetch `?page=`.
   */
  const gotoPageZeroBased = (zeroBasedPage: number): void => {
    if (serverSide) {
      // While a fetch is in flight `serverTotal` can be 0; do not clamp that
      // unknown total back to page 1.
      const next = zeroBasedPage + 1
      if (totalPages <= 0) {
        onPageChange?.(Math.max(1, next))
        return
      }
      onPageChange?.(Math.max(1, Math.min(totalPages, next)))
      return
    }
    const clamped = Math.max(
      0,
      Math.min(Math.max(totalPages - 1, 0), zeroBasedPage),
    )
    setCurrentPage(clamped)
  }

  /* Update data when input changes but preserve search and pagination
   * Uses flatMap to interleave normal and intersecting rows
   * Client-side mode only — server mode renders the fetched page as-is.
   */
  useEffect(() => {
    if (serverSide) return
    // Apply current search filter to new data
    let newData: DataItem[]
    if (searchTermRef.current) {
      const { results, scores } = performSearch(
        combinedData,
        headers,
        searchTermRef.current,
      )
      const filteredData = results
        .map((id) => combinedData.find((item) => item._searchId === id))
        .filter(Boolean) as DataItem[]

      setSearchScores(scores)
      newData = filteredData

      // Prevent unnecessary callbacks
      if (
        onFilteredDataChange &&
        JSON.stringify(filteredData.map((item) => item.id)) !==
          JSON.stringify(dataWithIds.map((item) => item.id))
      ) {
        onFilteredDataChange(filteredData)
      }
    } else {
      newData = dataWithIds

      // Call the callback with all data since nothing is filtered
      if (onFilteredDataChange) {
        onFilteredDataChange(dataWithIds)
      }
    }

    // Re-apply active sort if there is one
    if (activeSort !== null) {
      const header = headers[activeSort]
      newData = sortData(
        newData,
        header.key,
        header.type,
        sortDirection,
        header,
      )
    }

    setSortedData(newData)

    // Only adjust pagination if current page would be empty
    const newDataLength = searchTermRef.current
      ? performSearch(combinedData, headers, searchTermRef.current).results
          .length
      : dataWithIds.length

    if (
      newDataLength <= getCurrentPage() * maxDisplayedItems &&
      newDataLength > 0
    ) {
      const newTotalPages = Math.ceil(newDataLength / maxDisplayedItems)

      currentPageRef.current = newTotalPages - 1
    } else if (newDataLength === 0) {
      currentPageRef.current = 0
    }
  }, [
    dataWithIds,
    combinedData,
    headers,
    maxDisplayedItems,
    onFilteredDataChange,
    activeSort,
    sortDirection,
    serverSide,
  ])

  /* Column sorting handler
   * Manages sort state and direction
   * Client mode sorts locally and resets pagination; server mode only updates
   * the header UI state and forwards `{ key, direction }` so the caller can
   * fetch `?ordering=` (page reset is the caller's job).
   */
  const handleSort = (columnIndex: number): void => {
    const header = headers[columnIndex]
    let newDirection: SortDirection =
      activeSort === columnIndex
        ? sortDirection === 'asc'
          ? 'desc'
          : 'asc'
        : 'asc'

    setActiveSort(columnIndex)
    setSortDirection(newDirection)

    if (serverSide) {
      onSortChange?.({
        key: header.serverKey ?? header.key,
        direction: newDirection,
      })
      return
    }

    // Sort using the combined data to maintain relationships
    // Pass the full header object to access getSortValue if available
    const sorted = sortData(
      sortedData,
      header.key,
      header.type,
      newDirection,
      header,
    )

    setSortedData(sorted)
    setCurrentPage(0)
  }

  /* Render a single row based on type (normal or intersecting)
   * @param rowData - Data for the current row
   * @param rowIdx - Index of the row
   * @param isIntersecting - Whether this is an intersecting row
   * @returns JSX for the row
   */
  const renderGridRow = (
    rowData: DataItem,
    rowIdx: number,
    isIntersecting: boolean = false,
  ): ReactNode | ReactNode[] => {
    if (isIntersecting && renderIntersectingRow) {
      return (
        <SmoothFullWidthCell
          columns={columns}
          key={`intersecting-row-${rowData._originalIndex}`}
          isLastRow={rowIdx === displayData.length * 2 - 1}
        >
          {renderIntersectingRow(rowData, rowIdx, rowData._originalIndex!)}
        </SmoothFullWidthCell>
      )
    }

    const renderedCells = renderRow(rowData, rowIdx, rowData._originalIndex!)
    const hasExpansiveList = checkForExpansiveList(renderedCells)
    const isLastRow =
      rowIdx ===
      (renderIntersectingRow
        ? displayData.length * 2 - 1
        : displayData.length - 1)

    // Check if this row should be highlighted and with what color
    let isHighlighted = false
    let highlightColor: string | null = null

    if (highlightDataIdx !== null && highlightDataIdx !== undefined) {
      if (Array.isArray(highlightDataIdx)) {
        // New format: array of {idx, color} objects
        const highlightConfig = highlightDataIdx.find(
          (h) => h.idx === rowData._originalIndex,
        )

        if (highlightConfig) {
          isHighlighted = true
          highlightColor = highlightConfig.color
        }
      } else {
        // Legacy format: single index
        isHighlighted = rowData._originalIndex === highlightDataIdx
      }
    }

    return renderedCells.map((cell, cellIdx) => (
      <CellKeeper
        borderLeft={headers[cellIdx].borderLeft}
        key={`cell-${rowData._originalIndex}-${rowIdx}-${cellIdx}`}
        isLastRow={isLastRow}
        style={{
          ...(headers[cellIdx].style || {}),
          ...(headers[cellIdx].cellKeeperStyle || {}),
        }}
      >
        <EmptySpace hasExpansiveList={hasExpansiveList} />
        <Cell
          columns={columns}
          center={headers[cellIdx].center}
          isHighlighted={isHighlighted}
          highlightColor={highlightColor || undefined}
          style={headers[cellIdx].cellStyle}
        >
          <CellContent style={headers[cellIdx].cellContentStyle}>
            {cell}
          </CellContent>
        </Cell>
      </CellKeeper>
    ))
  }

  /* Generate all rows including intersecting rows if enabled
   * Uses flatMap to interleave normal and intersecting rows
   */
  const generateRows = (): ReactNode[] => {
    if (renderIntersectingRow) {
      return displayData.flatMap((rowData, idx) => {
        const originalIdx = rowData._originalIndex!

        const normalRow = renderGridRow(rowData, idx * 2, false)

        // Always generate intersecting row if renderIntersectingRow is provided
        const intersectingRow = renderGridRow(rowData, idx * 2 + 1, true)

        // Only include intersecting row if it returns content
        const intersectingContent = renderIntersectingRow(
          rowData,
          idx * 2 + 1,
          originalIdx,
        )

        return intersectingContent ? [normalRow, intersectingRow] : [normalRow]
      })
    }

    return displayData.map((rowData, idx) => renderGridRow(rowData, idx, false))
  }

  // Search box visibility: client mode hides it for single-row datasets;
  // server mode always shows it when searchable so an empty result can be cleared.
  const showSearchBox = isSearchable && (serverSide ? true : data.length > 1)

  return (
    <GridWrapper data-testid={dataTestId} {...props}>
      <GridTop>
        <GridHeaderContainer paddingInGridHeader={paddingInGridHeader}>
          <GridHeaderLeft>{headerInfo}</GridHeaderLeft>
          {showSearchBox && (
            <GridHeaderRight>
              {(serverSide || data.length > 1) && (
                <InputGroup inside style={{ width: '100%', height: '2.3rem' }}>
                  <Input
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(value: string) => {
                      userChangedSearchRef.current = true
                      setSearchTerm(value)
                    }}
                  />
                  <InputGroup.Button>
                    <SearchIcon />
                  </InputGroup.Button>
                </InputGroup>
              )}
            </GridHeaderRight>
          )}
        </GridHeaderContainer>
      </GridTop>

      {data.length > 0 ? (
        <>
          <GridArea ref={gridContainerRef} style={{ height: 'auto' }}>
            <GridContainer
              ref={gridRef}
              columns={columns}
              style={{
                ...(calculatedGridTemplate &&
                !gridContainerStyle?.gridTemplateColumns
                  ? {
                      gridTemplateColumns: calculatedGridTemplate,
                    }
                  : {}),
                ...gridContainerStyle,
              }}
            >
              {/* Render headers with column-specific styles */}
              {headers.map((header, idx) =>
                header.isSortable ? (
                  <HeaderCell
                    columns={columns}
                    key={`header-${idx}`}
                    onClick={() => handleSort(idx)}
                    className={activeSort === idx ? 'active' : ''}
                    borderLeft={header.borderLeft}
                    center={header.center}
                    style={{
                      ...(header.style || {}),
                      ...(header.headerCellStyle || {}),
                    }}
                    isSortable={true}
                  >
                    <CellContent style={header.cellContentStyle}>
                      {header.overrideJSX || header.title}
                      <SortIcon>
                        {activeSort === idx ? (
                          sortDirection === 'asc' ? (
                            <CustomIcons.ChevronUpIcon
                              color={theme.colors.veryVeryFadedText}
                              size={'xxs'}
                            />
                          ) : (
                            <CustomIcons.ChevronDownIcon
                              color={theme.colors.veryVeryFadedText}
                              size={'xxs'}
                            />
                          )
                        ) : (
                          <CustomIcons.ChevronUpIcon
                            color={theme.colors.veryVeryFadedText}
                            size={'xxs'}
                          />
                        )}
                      </SortIcon>
                    </CellContent>
                  </HeaderCell>
                ) : (
                  <HeaderCell
                    columns={columns}
                    key={`header-${idx}`}
                    borderLeft={header.borderLeft}
                    center={header.center}
                    style={{
                      ...(header.style || {}),
                      ...(header.headerCellStyle || {}),
                    }}
                    isSortable={false}
                  >
                    <CellContent style={header.cellContentStyle}>
                      {header.overrideJSX || header.title}
                    </CellContent>
                  </HeaderCell>
                ),
              )}

              {/* Render all rows using the new generator */}
              {generateRows()}
            </GridContainer>
            <UniversalLoader show={loading} content={loadingMessage} />
          </GridArea>

          <GridBottom>
            {/* Pagination controls */}
            {(serverSide ? effectiveTotal > 0 : sortedData.length > 0) &&
              totalPages > 1 && (
                <PaginationContainer $compact={compactPagination}>
                  <NavigationButton
                    type="button"
                    $compact={compactPagination}
                    onClick={() => gotoPageZeroBased(currentPageZeroBased - 1)}
                    disabled={currentPageZeroBased === 0}
                  >
                    {compactPagination ? 'Prev' : 'Previous'}
                  </NavigationButton>

                  <PagesContainer $compact={compactPagination}>
                    {getPageNumbers(
                      currentPageZeroBased,
                      totalPages,
                      compactPagination,
                    ).map((pageNum, idx) => {
                      return pageNum === '...' ? (
                        <PageIndicator
                          key={`ellipsis-${idx}`}
                          $compact={compactPagination}
                        >
                          ...
                        </PageIndicator>
                      ) : (
                        <NumberButton
                          type="button"
                          $compact={compactPagination}
                          key={`page-${pageNum}`}
                          onClick={() => gotoPageZeroBased(pageNum as number)}
                          className={
                            currentPageZeroBased === pageNum ? 'active' : ''
                          }
                        >
                          {(pageNum as number) + 1}
                        </NumberButton>
                      )
                    })}
                  </PagesContainer>

                  <NavigationButton
                    type="button"
                    $compact={compactPagination}
                    onClick={() => gotoPageZeroBased(currentPageZeroBased + 1)}
                    disabled={currentPageZeroBased >= totalPages - 1}
                  >
                    Next
                  </NavigationButton>
                </PaginationContainer>
              )}
          </GridBottom>
        </>
      ) : data.length <= 0 && loading ? (
        <GridArea>
          <UniversalLoader
            show={loading}
            content={loadingMessage}
            useParentSize={false}
          />
        </GridArea>
      ) : (
        <GridArea>
          <NoItemsMessage>{emptyStateMessage}</NoItemsMessage>
        </GridArea>
      )}
    </GridWrapper>
  )
}

export default VersatileGrid
